import { Badge, Loader, Tooltip } from '@mantine/core'

import { DataMigrationPhase } from 'src/graphql/types/dataMigrations'
import { formatSnakeValue } from 'src/lib/formatters'
import {
  getPhaseColor,
  isActivePhase,
} from 'src/pages/SettingsPage/tabs/dataMigrationTab/lib/migrationDisplay'

interface MigrationPhaseBadgeProps {
  phase: string
  /** Shown as a tooltip on FAILED badges. */
  errorMessage?: string | null
  /** Active phase whose heartbeat has gone quiet — renders an amber dot. */
  stalled?: boolean
}

/**
 * Phase display convention:
 * -ING phases = actively running (blue badge + subtle spinner, stall alarm),
 * parked phases = gray "waiting" badge, COMPLETED = green, FAILED = red
 * (errorMessage in tooltip), CANCELLED = gray.
 */
const MigrationPhaseBadge = ({
  phase,
  errorMessage,
  stalled = false,
}: MigrationPhaseBadgeProps) => {
  const active = isActivePhase(phase)

  const badge = (
    <Badge
      variant="light"
      color={getPhaseColor(phase)}
      size="sm"
      data-testid="migration-phase-badge"
      data-phase={phase}
      leftSection={
        active ? (
          <Loader size={10} color="blue" data-testid="phase-badge-loader" />
        ) : undefined
      }
    >
      {formatSnakeValue(phase)}
    </Badge>
  )

  return (
    <span className="inline-flex items-center gap-1.5">
      {phase === DataMigrationPhase.FAILED && errorMessage ? (
        <Tooltip label={errorMessage} withArrow multiline maw={360}>
          {badge}
        </Tooltip>
      ) : (
        badge
      )}
      {active && stalled && (
        <Tooltip
          label="No heartbeat for over 30 minutes — this migration may be stalled"
          withArrow
        >
          <span
            data-testid="phase-badge-stalled"
            className="h-2 w-2 rounded-full bg-doubleNickel-warning-400"
          />
        </Tooltip>
      )}
    </span>
  )
}

export default MigrationPhaseBadge
